import { useEffect, useState } from "react";
import { Link, useParams } from "react-router-dom";
import { formatUsd } from "@haulsheet/core";
import { Button } from "../components/ui/Button";
import { useAuthStore } from "../store/authStore";
import { getSyncClient } from "../sync/SyncClient";
import {
  SETTLEMENT_STATUS_LABEL,
  formatPeriod,
  getSettlement,
  settlementAction,
  type SettlementDetail,
} from "../api/settlements";

/** One pay week: its loads and totals; the owner finalizes it, then marks it paid. */
export function SettlementScreen() {
  const { id } = useParams();
  const me = useAuthStore((s) => s.me);
  const [data, setData] = useState<SettlementDetail | null>(null);
  const [loadError, setLoadError] = useState<string | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);

  useEffect(() => {
    if (!id) return;
    let alive = true;
    getSettlement(id)
      .then((d) => alive && setData(d))
      .catch((e: unknown) => alive && setLoadError(e instanceof Error ? e.message : "Couldn't load this pay week."));
    return () => {
      alive = false;
    };
  }, [id]);

  async function act(action: "finalize" | "paid") {
    if (!id || busy) return;
    setBusy(true);
    setError(null);
    try {
      const d = await settlementAction(id, action);
      setData({ settlement: d.settlement, loads: d.loads });
      // Paid loads (and their history) come back through sync.
      void getSyncClient().syncNow();
    } catch (e) {
      setError(e instanceof Error ? e.message : "That didn't work. Try again.");
    } finally {
      setBusy(false);
    }
  }

  const s = data?.settlement;
  const isOwner = me?.role === "owner";
  const readOnly = me?.plan.readOnly ?? false;

  return (
    <div className="stack">
      <Link to="/money" className="muted back-link">
        ← Money
      </Link>
      <h1 className="screen-title">Pay week</h1>
      {!data ? (
        <p className="muted">{loadError ? "Pay weeks show when you're online." : "Loading…"}</p>
      ) : (
        <>
          <div className="card stack" data-testid="settlement-card">
            <div className="row" style={{ justifyContent: "space-between", alignItems: "center" }}>
              <strong>{formatPeriod(s!.periodStart, s!.periodEnd)}</strong>
              <span className="chip" data-testid="settlement-status">
                {SETTLEMENT_STATUS_LABEL[s!.status] ?? s!.status}
              </span>
            </div>
            <div className="list-item" style={{ padding: 0 }}>
              <span>Keep from loads</span>
              <span data-testid="settlement-keep">{formatUsd(s!.totals.keepCents)}</span>
            </div>
            <div className="list-item" style={{ padding: 0 }}>
              <span>Detention</span>
              <span data-testid="settlement-detention">{formatUsd(s!.totals.detentionCents)}</span>
            </div>
            <div className="list-item" style={{ padding: 0 }}>
              <strong>Total</strong>
              <strong data-testid="settlement-total">{formatUsd(s!.totals.totalCents)}</strong>
            </div>
            {isOwner && !readOnly && s!.status === "draft" ? (
              <Button variant="primary" data-testid="settlement-finalize" disabled={busy} onClick={() => void act("finalize")}>
                {busy ? "Saving…" : "Finalize this week"}
              </Button>
            ) : null}
            {isOwner && !readOnly && s!.status === "final" ? (
              <Button variant="primary" data-testid="settlement-paid" disabled={busy} onClick={() => void act("paid")}>
                {busy ? "Saving…" : "Mark paid"}
              </Button>
            ) : null}
            {isOwner && readOnly && s!.status !== "paid" ? (
              <p className="muted" style={{ margin: 0 }}>
                Your plan is read-only, so pay weeks can&apos;t be finalized or paid. Renew in Settings → Plan.
              </p>
            ) : null}
            {!isOwner && s!.status !== "paid" ? (
              <p className="muted" style={{ margin: 0 }}>
                The owner finalizes this week and marks it paid.
              </p>
            ) : null}
            {s!.status === "draft" && isOwner ? (
              <p className="muted" style={{ margin: 0 }}>
                Finalize when the week is done. After that its loads can&apos;t be undone, and you can mark it paid.
              </p>
            ) : null}
            {error ? (
              <p className="legal-flag" role="alert" style={{ margin: 0 }}>
                {error}
              </p>
            ) : null}
          </div>
          <div className="card" style={{ padding: 0 }}>
            {data.loads.length === 0 ? (
              <p className="muted" style={{ padding: "1rem", margin: 0 }}>
                No loads on this week.
              </p>
            ) : (
              data.loads.map((l) => (
                <Link key={l.id} to={`/loads/${l.id}`} className="list-item" data-testid="settlement-load">
                  <div>
                    <div>
                      {l.origin || "Origin"} → {l.destination || "Destination"}
                    </div>
                    <div className="muted">
                      <span className="chip">{l.status === "paid" ? "Paid" : "Delivered"}</span>
                      {l.detentionCents > 0 ? ` · ${formatUsd(l.detentionCents)} detention` : ""}
                    </div>
                  </div>
                  <strong>{formatUsd(l.keepCents)}</strong>
                </Link>
              ))
            )}
          </div>
        </>
      )}
    </div>
  );
}
